import { Component, HostListener } from "@angular/core";
import { ColDef, ExcelStyle, GridApi, GridOptions , GridReadyEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute, Router } from "@angular/router";
import { ApiService } from "../../services";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { ModalService } from "src/app/shared/modal.service";
import { GridViewBaseComponent } from "../../utility/grid-view-base.component";
import { SharedDataService } from "../../services/sharedDataService";
import { exportExcel, getExportedExcelFileNameSuffix } from "src/app/utility/common-helper";
import { ColParams } from "src/app/shared/grid/ag-grid";
import { PfPlansColumnDefinition } from "./ag-grid-column-definition";
import { PlanOnboardingModalOptions } from "../plans/modals/plan-onboarding-modal/plan-onboarding-modal.component";
import { PfPlan } from "src/app/generated-models/PfPlan";
import { PfPlanOperationTypeEnum } from "src/app/shared/enums/pf-enums";
import { BlueprintType, IPfCapacityPlan } from "src/app/npr-request.model";

@Component({
  standalone: false,
  templateUrl: "./pf-plans.component.html",
  styleUrls: ["../../styles.scss", "./pf.scss"],
})
export class PfPlansComponent extends GridViewBaseComponent {
  gridOptions: GridOptions;
  gridApi: GridApi;
  initPageSize = 15;
  showMyServiceTeamPlansOnly: boolean = false;
  myPfPlanServiceTreeIds: string[] = [];
  rowData: IPfCapacityPlan[] = [];
  headerStyle: ExcelStyle[] = [
    {
      id: "header",
      font: {
        bold: true,
      },
    },
  ];

  keyFilterStats = "filter:region-agnostic-plans";

  message: string;

  constructor(
    protected apiService: ApiService,
    private modalService: ModalService,
    private loadingService: LoadingScreenService,
    protected sharedDataService: SharedDataService,
    private notificationService: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super(apiService, sharedDataService);
  }

  async ngOnInit() {
    this.routeToPfPlanEditPage();
    super.InitAgGrid(PfPlansColumnDefinition);

    this.myPfPlanServiceTreeIds = (await this.apiService.getMyPfPlans().toPromise()).map(plan => plan.ServiceTreeId);
    if (this.userProfile?.IsApprover) {
      this.showMyServiceTeamPlansOnly = false;
    }
    PfPlansColumnDefinition.forEach( (colDef: ColDef) => {
      if (colDef.headerName === 'Custom Filter') {
        colDef.valueGetter = (params: ColParams<PfPlan, void>) => {
          if (!this.showMyServiceTeamPlansOnly) {
            return true;
          }
          const row = params.data as PfPlan;
          return this.myPfPlanServiceTreeIds.some(service => row.ServiceTreeId === service);
        }
      }
    });

    this.gridApi?.setGridOption('columnDefs', PfPlansColumnDefinition);
  }

  onGridReady(params: GridReadyEvent) {
    super.onGridReady(params);
    this.gridApi.setFilterModel({
      customFilter: { values: ["true"] },
    });
  }

  refreshData() {
    this.loadingService.setLoading(true);
    this.apiService.getAllPfPlans().subscribe(
      (response) => {
        const plans: IPfCapacityPlan[] = [];
        response.forEach((plan) => {
          const row: IPfCapacityPlan = {
            ServiceTeam: plan.ServiceTeam,
            ServiceTreeId: plan.ServiceTreeId,
            Ring: plan.Ring,
            Version: plan.Version,
            Status: plan.Status,
            CreatedBy: "",
            CreatedTime: new Date(0),
            Comment: ""
          };
          const createOperation = plan.Operations.find(p => p.Operation === PfPlanOperationTypeEnum.Create);
          if (createOperation) {
            row.CreatedBy = createOperation.By;
            row.CreatedTime = createOperation.At;
          }
          plans.push(row);
        });
        this.rowData = plans;
        if (this.gridApi) {
          this.gridApi.setGridOption("rowData", this.rowData);
        }
        this.loadingService.setLoading(false);
        super.setupFilterByLocalData();
      },
      (e: any) => {
        this.rowData = [];
        this.loadingService.setLoading(false);
        this.notificationService.error(e);
      }
    );
  }

  @HostListener("window:resize")
  onResize() {
    setTimeout(() => {
      //this.gridApi.sizeColumnsToFit();
      this.gridApi.autoSizeAllColumns();
    }, 100);
  }

  onShowMyServiceTeamPlansOnlyChanged() {
    this.gridApi.onFilterChanged();
  }

  // reuse Plan Onboarding Modal with save function updated only
  async showOnboardingDialog() {
    const options: PlanOnboardingModalOptions = {
      save: this.onboardNewPfPlan.bind(this),
      type: BlueprintType.PF,
    };
    await this.modalService.planOnboardingModal(options);
  }

  onboardNewPfPlan(serviceTreeId: string) {
    this.loadingService.setLoading(true);

    this.apiService.onboardNewPfPlan(serviceTreeId).subscribe(
      () => {
        this.message = `New Pf Plan has been onboarded successfully.`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (err: unknown) => {
        this.message = `Failed to onboard new pf plan. ${err}`;
        this.notificationService.error(this.message);
        this.loadingService.setLoading(false);
      },
      () => {
        this.refreshData();
      }
    );
  }

  exportExcel() {
    const fileName = "PfBlueprints-" + getExportedExcelFileNameSuffix();
    const sheetName = "PF Blueprints";

    this.loadingService.setLoading(true);
    exportExcel(this.gridApi, fileName, sheetName);
    this.loadingService.setLoading(false);
  }

  routeToPfPlanEditPage() {
    // deep link to edit pf plan page if service query param is provided
    this.route.queryParams.subscribe((params) => {
      if (params.service && params.service.trim() != "") {
        this.router.navigate([`/pf/services/${params.service}/editor`]);
      }
    });
  }
}
